import React from "react";
import { useField } from "formik";
import {
  TextField,
  FormHelperText,
  FormControl,
} from "@mui/material";

interface NumberInputProps {
  name: string;
  label?: string;
  required?: boolean;
  floatingLabel?: boolean;
  minDigits?: number;
  maxDigits?: number;
  allowNegative?: boolean;
  allowDecimal?: boolean;
  [key: string]: any; // other TextField props
}

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  required,
  floatingLabel = false,
  minDigits,
  maxDigits,
  allowNegative = false,
  allowDecimal = false,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const errorText = meta.error && meta.touched ? meta.error : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (allowNegative && value.startsWith("-")) {
      value = "-" + value.slice(1).replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, "");
    } else {
      value = value.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, "");
    }

    if (allowDecimal) {
      if (value.startsWith(".")) {
        value = "";
      }

      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts[1];
      }
    } else {
      value = value.replace(/\./g, "");
    }

    const digitsOnly = value.replace(/[^0-9]/g, "");
    if (maxDigits && digitsOnly.length > maxDigits) {
      return;
    }

    helpers.setValue(value);
  };

  const renderLabel = (
    <>
      {label}
      {required && <span style={{ color: "red" }}>*</span>}
    </>
  );

  return (
    <FormControl fullWidth error={!!errorText} sx={{ mb: 2 }}>
      {floatingLabel ? (
        <TextField
          {...field}
          {...props}
          value={field.value || ""}
          onChange={handleChange}
          variant="outlined"
          label={renderLabel}
          error={!!errorText}
          size="small"
          fullWidth
        />
      ) : (
        <>
          {label && <label>{renderLabel}</label>}
          <TextField
            {...field}
            {...props}
            value={field.value || ""}
            onChange={handleChange}
            variant="outlined"
            error={!!errorText}
            size="small"
            fullWidth
          />
        </>
      )}
      {errorText && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

export default NumberInput;
